/* eslint-disable no-unused-vars */
// R10e35b-8.5-006: ESLint flat config doesn't detect React.lazy() component usage in JSX Routes.
// All lazy-loaded components and react-router-dom imports ARE used in AppRoutes below.
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import TermsAcceptanceGate from './components/TermsAcceptanceGate';  // E-ii.3
import { GuidedTourProvider } from './components/GuidedTourProvider';
import { Toaster } from './components/ui/sonner';
// R10e36.73.2-F647: Import DashboardLayout for Trust Account routes
import DashboardLayout from './components/layout/DashboardLayout';
// R10e36.71.1-F773: DesktopOnly wrapper for mobile gating
import { DesktopOnly } from './components/DesktopOnly';
// R10e36.71.1-F773: SudoModal for sensitive action re-authentication
import SudoModal, { useSudoModal } from './components/SudoModal';
import './App.css';

// R10e36.73.3-A2: Removed @tanstack/react-query (zero useQuery/useMutation calls in codebase)

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// ====================================
// LAZY-LOADED PAGE COMPONENTS
// Code splitting for better initial load performance
// ====================================

// Main Pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const AcceptInvitePage = React.lazy(() => import('./pages/AcceptInvitePage'));
const InviteUserPage = React.lazy(() => import('./pages/InviteUserPage'));
const CollectorDashboard = React.lazy(() => import('./pages/CollectorDashboard'));
const MunicipalDashboard = React.lazy(() => import('./pages/MunicipalDashboard'));
// R10e36.52-A1: PublicTaxPortal replaces PublicPortal, CertificatePortal, TitleCompanyPortal
const PublicTaxPortal = React.lazy(() => import('./pages/PublicTaxPortal'));
const ParcelManagement = React.lazy(() => import('./pages/ParcelManagement'));
const PaymentProcessing = React.lazy(() => import('./pages/PaymentProcessing'));
// R10e36.53.3-F393: CertificationRequests deleted — /certifications redirects to /certificate-admin
const ImportWizard = React.lazy(() => import('./pages/ImportWizard'));
const MigrationWizard = React.lazy(() => import('./pages/MigrationWizard'));
const AuditLogs = React.lazy(() => import('./pages/AuditLogs'));
const Settings = React.lazy(() => import('./pages/Settings'));
const CollectorOperations = React.lazy(() => import('./pages/CollectorOperations'));
const JurisdictionRoster = React.lazy(() => import('./pages/JurisdictionRoster'));
const CollectorOnboarding = React.lazy(() => import('./pages/CollectorOnboarding'));
const ReportCenter = React.lazy(() => import('./pages/ReportCenter'));
const CertificateAdmin = React.lazy(() => import('./pages/CertificateAdmin'));
// R10e36.52.1-D6: Certificate Pricing Admin
const CertificatePricingAdmin = React.lazy(() => import('./pages/CertificatePricingAdmin'));
// R10e36.52-A1: CertificatePortal removed - functionality moved to PublicTaxPortal
const PaymentReviewQueue = React.lazy(() => import('./pages/PaymentReviewQueue'));
// R10e36.60-F450: TaxStatusCertification retired - functionality in Certificate Management
// R10e36.58-F437: Mail Queue page
const MailQueue = React.lazy(() => import('./pages/MailQueue'));
const CollectorSettings = React.lazy(() => import('./pages/CollectorSettings'));
const ProfessionalPortal = React.lazy(() => import('./pages/ProfessionalPortal'));
const FirstTimeSetupWizard = React.lazy(() => import('./pages/FirstTimeSetupWizard'));
const JurisdictionTransfer = React.lazy(() => import('./pages/JurisdictionTransfer'));
const AssessmentChanges = React.lazy(() => import('./pages/AssessmentChanges'));
const ChargeAssignment = React.lazy(() => import('./pages/ChargeAssignment'));
const RemittanceReport = React.lazy(() => import('./pages/RemittanceReport'));
const MillageRateAdjustment = React.lazy(() => import('./pages/MillageRateAdjustment'));
// PC132 (Deploy 7 Batch 3 hotfix): standalone dual-cycle billing calendar editor
const BillingCalendarEditor = React.lazy(() => import('./pages/BillingCalendarEditor'));
// PC135: SCHOOL installment plan configurator (PA Act 1)
const InstallmentPlanConfig = React.lazy(() => import('./pages/InstallmentPlanConfig'));

// R10e36.50-F285: Payment Posting Policy Admin
const PaymentPostingPolicyAdmin = React.lazy(() => import('./pages/PaymentPostingPolicyAdmin'));

// R10e36-012: BOA Reconciliation workflow
const BOAReconciliation = React.lazy(() => import('./pages/BOAReconciliation'));

// R10e35b-8.3-009: Outstanding Bills Page
const OutstandingBills = React.lazy(() => import('./pages/OutstandingBills'));

// R10e31-009: Bill Run QC Split-Screen
const BillRunQC = React.lazy(() => import('./pages/BillRunQC'));

// R10e36.79-F995: Bill Run Output Page (persistent output actions)
const BillRunOutput = React.lazy(() => import('./pages/BillRunOutput'));

// R10e32-004: Bill Template Editor
const BillTemplateList = React.lazy(() => import('./pages/BillTemplateList'));
const BillTemplateEditor = React.lazy(() => import('./pages/BillTemplateEditor'));

// R10e36.66.2-F596: Walk-in cert generation page
const CertificateGenerate = React.lazy(() => import('./pages/CertificateGenerate'));

// R10e36.69: Security Pages (MFA, Password Reset)
const MFAEnrollPage = React.lazy(() => import('./pages/MFAEnrollPage'));
const MFAVerifyPage = React.lazy(() => import('./pages/MFAVerifyPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const SecurityEventsPage = React.lazy(() => import('./pages/SecurityEventsPage'));

// R10e36.73-F643: Trust Account Pages
// R10e36.73.6-F674: Added TrustAccountLayout for tab navigation
const TrustAccountLayout = React.lazy(() => import('./pages/trust-account/TrustAccountLayout'));
const TrustAccountDashboard = React.lazy(() => import('./pages/trust-account/TrustAccountDashboard'));
const TrustTransactions = React.lazy(() => import('./pages/trust-account/TrustTransactions'));
const TrustRemittances = React.lazy(() => import('./pages/trust-account/TrustRemittances'));
const TrustReconciliation = React.lazy(() => import('./pages/trust-account/TrustReconciliation'));
const TrustClosePeriod = React.lazy(() => import('./pages/trust-account/TrustClosePeriod'));
const TrustReports = React.lazy(() => import('./pages/trust-account/TrustReports'));
// R10e36.68.4-F831: Commission Invoicing for Design A (GROSS_REMIT) collectors
const CommissionInvoices = React.lazy(() => import('./pages/trust-account/CommissionInvoices'));

// R10e36.73.11-F729: Historical Import Page
const HistoricalImportPage = React.lazy(() => import('./pages/HistoricalImportPage'));

// Admin Portal Pages
const AdminSetup = React.lazy(() => import('./pages/admin/AdminSetup'));
// R10e35b-8.1-004: Removed AdminLogin import - /ops-admin/login redirects to /login
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const PlatformOverview = React.lazy(() => import('./pages/admin/PlatformOverview'));  // R10e35b-3-016
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const CollectorManagement = React.lazy(() => import('./pages/admin/CollectorManagement'));
const ApprovalQueue = React.lazy(() => import('./pages/admin/ApprovalQueue'));
const SystemAdminAnalytics = React.lazy(() => import('./pages/SystemAdminAnalytics'));
// R9 (2026-09-26): SystemAdmin certificate-issuance analytics
const CertificateAnalytics = React.lazy(() => import('./pages/CertificateAnalytics'));
const SubscriptionBilling = React.lazy(() => import('./pages/SubscriptionBilling'));  // C3
const CollectorEmails = React.lazy(() => import('./pages/admin/CollectorEmails'));  // C4
const IntakeForm = React.lazy(() => import('./pages/intake/IntakeForm'));  // A20 public private-link form
const CollectorIntakes = React.lazy(() => import('./pages/admin/CollectorIntakes'));  // A20 admin

// Registration Pages
const CollectorRegister = React.lazy(() => import('./pages/CollectorRegister'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const PendingApproval = React.lazy(() => import('./pages/PendingApproval'));

// R10e36.69.1.1-F503-R4: Helper to decode JWT scope claim
function decodeTokenScope(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.scope || null;
  } catch {
    return null;
  }
}

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // R10e36.69.1.1-F503-R4: If user has limited mfa_setup scope, restrict access to enrollment only
  const token = localStorage.getItem('access_token');
  const tokenScope = decodeTokenScope(token);
  if (tokenScope === 'mfa_setup') {
    // Limited scope token can only access MFA enrollment
    return <Navigate to="/mfa/enroll" replace />;
  }

  const userRole = user?.user_role || user?.role;
  if (allowedRoles && user && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // E-ii.3: one-time Terms of Service acceptance (skips SystemAdmin server-side)
  return <TermsAcceptanceGate>{children}</TermsAcceptanceGate>;
};

// E-i.1 (2026-09-23): order.paezproptax.com serves the public certificate portal from this
// same build. On that host, "/" and "/login" go straight to the portal.
const IS_ORDER_HOST =
  typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('order.');
// Unlisted SystemAdmin sign-in path. Obscurity only — the real controls are MFA,
// SYSTEMADMIN_IP_ALLOWLIST, and the backend rejecting SystemAdmin on the normal login.
const ADMIN_LOGIN_PATH = process.env.REACT_APP_ADMIN_LOGIN_PATH || '/rect-ops-signin';

function AppRoutes() {
  return (
    <Routes>
      {/* R10e35b-3-016: Admin routes consolidated - redirects to new structure */}
      <Route path="/ops-admin" element={<Navigate to="/login" replace />} />
      <Route path="/ops-admin/setup" element={<AdminSetup />} />
      <Route path="/ops-admin/login" element={<Navigate to="/login" replace />} />
      <Route path="/ops-admin/dashboard" element={<Navigate to="/admin/platform-overview" replace />} />
      <Route path="/ops-admin/users" element={<Navigate to="/admin/users" replace />} />
      <Route path="/ops-admin/collectors" element={<Navigate to="/admin/collectors" replace />} />
      <Route path="/ops-admin/approvals" element={<Navigate to="/admin/approvals" replace />} />
      
      {/* R10e35b-3-016: New admin routes within DashboardLayout */}
      <Route path="/admin/platform-overview" element={
        <ProtectedRoute allowedRoles={['SystemAdmin']}>
          <PlatformOverview />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['SystemAdmin']}>
          <UserManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/collectors" element={
        <ProtectedRoute allowedRoles={['SystemAdmin']}>
          <CollectorManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/approvals" element={
        <ProtectedRoute allowedRoles={['SystemAdmin']}>
          <ApprovalQueue />
        </ProtectedRoute>
      } />
      
      {/* Registration Routes */}
      <Route path="/register" element={<CollectorRegister />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/pending-approval" element={<PendingApproval />} />
      
      {/* Main Application Routes */}
      <Route path="/login" element={IS_ORDER_HOST ? <Navigate to="/portal" replace /> : <LoginPage />} />
      {!IS_ORDER_HOST && <Route path={ADMIN_LOGIN_PATH} element={<LoginPage adminEntry />} />}
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      {/* A20 (2026-09-27): private-link Collector intake — no login */}
      <Route path="/intake/:token" element={<IntakeForm />} />
      
      {/* R10e36.69: Security Routes (MFA, Password Reset) */}
      {/* R10e36.69.1.1-F503-R4: Remove ProtectedRoute wrap — MFAEnrollPage self-checks via F503-R3 */}
      <Route path="/mfa/enroll" element={<MFAEnrollPage />} />
      <Route path="/mfa/verify" element={<MFAVerifyPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/admin/security-events" element={
        <ProtectedRoute allowedRoles={['SystemAdmin']}>
          <SecurityEventsPage />
        </ProtectedRoute>
      } />
      
      {/* R10e36.52-A1: Unified public portal route */}
      <Route path="/portal" element={<PublicTaxPortal />} />
      {/* R10e36.52-A1: Backward compat redirects for legacy bookmarks */}
      <Route path="/public" element={<Navigate to="/portal" replace />} />
      <Route path="/certificates" element={<Navigate to="/portal?tab=certificate" replace />} />
      {/* PC78 2026-08-30: Stripe success/cancel landing — PublicTaxPortal reads
          query params (status, cert_request_id, access_token) and shows a status card. */}
      <Route path="/public/certificates/status" element={<PublicTaxPortal />} />
      
      <Route
        path="/admin/invite-user"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <InviteUserPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <SystemAdminAnalytics />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <CollectorDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.64-F518: Municipal Dashboard removed - redirect to main dashboard */}
      <Route
        path="/municipal"
        element={<Navigate to="/dashboard" replace />}
      />
      
      {/* R10e36.52-A1: /title-company now redirects to unified portal */}
      <Route
        path="/title-company"
        element={<Navigate to="/portal?tab=portfolio" replace />}
      />
      
      <Route
        path="/collector/authorities"
        element={<Navigate to="/admin/roster" replace />}
      />
      
      <Route
        path="/parcels"
        element={
          <ProtectedRoute>
            <ParcelManagement />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/payments"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'CollectorStaff']}>
            <PaymentProcessing />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/payment-review"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <PaymentReviewQueue />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.60-F450: Retired — legacy page replaced by Certificate Management */}
      <Route path="/tax-status" element={<Navigate to="/certificate-admin" replace />} />
      
      {/* R10e36.53.3-F393: Redirect legacy /certifications to /certificate-admin */}
      <Route
        path="/certifications"
        element={<Navigate to="/certificate-admin" replace />}
      />
      
      <Route
        path="/imports"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'CollectorStaff']}>
            <ImportWizard />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.71.1-F778: Data Migration - desktop only */}
      <Route
        path="/migrations"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin']}>
            <DesktopOnly pageName="Data Migration">
              <MigrationWizard />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/operations"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <DesktopOnly pageName="Collector Operations">
              <CollectorOperations />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      {/* R10e31-009: Bill Run QC Split-Screen */}
      <Route
        path="/collector/bill-runs/:billRunId/qc"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <DesktopOnly pageName="Bill Run QC Review">
              <BillRunQC />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.79-F995: Bill Run Output Page */}
      <Route
        path="/collector/bill-runs/:billRunId/output"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <BillRunOutput />
          </ProtectedRoute>
        }
      />
      
      {/* R10e32-004: Bill Template Routes */}
      <Route
        path="/collector/billing/templates"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin']}>
            <DesktopOnly pageName="Bill Templates">
              <BillTemplateList />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collector/billing/templates/new"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin']}>
            <DesktopOnly pageName="Bill Template Editor">
              <BillTemplateEditor mode="create" />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collector/billing/templates/:templateId/edit"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin']}>
            <DesktopOnly pageName="Bill Template Editor">
              <BillTemplateEditor mode="edit" />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.71.1-F778: Collector Settings - desktop only */}
      <Route
        path="/collector/settings"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <DesktopOnly pageName="Collector Settings">
              <CollectorSettings />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <ReportCenter />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.71.1-F778: Charge Types - desktop only */}
      <Route
        path="/collector/charges"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <DesktopOnly pageName="Charge Types">
              <ChargeAssignment />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/collector/remittance"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin']}>
            <RemittanceReport />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36-012: BOA Reconciliation workflow */}
      <Route
        path="/collector/boa-reconciliation"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <DesktopOnly pageName="BOA Reconciliation">
              <BOAReconciliation />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/professional"
        element={
          <ProtectedRoute allowedRoles={['ProfessionalUser', 'SystemAdmin', 'TitleCompanyUser']}>
            <ProfessionalPortal />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.71.1-F778: Certificate Admin - desktop only */}
      <Route
        path="/certificate-admin"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <DesktopOnly pageName="Certificate Admin">
              <CertificateAdmin />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.66.2-F596: Walk-in cert generation route */}
      <Route
        path="/certificates/generate"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <CertificateGenerate />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.58-F437: Mail Queue route - R10e36.71.1-F778: desktop only */}
      <Route
        path="/collector/mail-queue"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin', 'CollectorStaff']}>
            <DesktopOnly pageName="Mail Queue">
              <MailQueue />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.52.1-D6: Certificate Pricing Admin */}
      <Route
        path="/admin/certificate-pricing"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <CertificatePricingAdmin />
          </ProtectedRoute>
        }
      />
      {/* R9 (2026-09-26): SystemAdmin certificate-issuance analytics */}
      <Route
        path="/admin/certificate-analytics"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <CertificateAnalytics />
          </ProtectedRoute>
        }
      />
      {/* A20 (2026-09-27): SystemAdmin Collector intakes */}
      <Route
        path="/admin/intakes"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <CollectorIntakes />
          </ProtectedRoute>
        }
      />
      {/* C3 (2026-09-27): SystemAdmin subscription billing */}
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <SubscriptionBilling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/collector-emails"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <CollectorEmails />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin', 'CollectorAdmin']}>
            <DesktopOnly pageName="Audit Logs">
              <AuditLogs />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.73-F643: Trust Account Routes */}
      {/* R10e36.73.2-F647: Wrapped in DashboardLayout */}
      {/* R10e36.73.6-F674: Nested under TrustAccountLayout with tab navigation */}
      {/* R10e36.71.1-F778: Trust Account stays mobile-accessible per corrected scope */}
      <Route
        path="/trust-account"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'CollectorStaff', 'SystemAdmin']}>
            <DashboardLayout>
              <TrustAccountLayout />
            </DashboardLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<TrustAccountDashboard />} />
        <Route path="dashboard" element={<TrustAccountDashboard />} />
        <Route path="transactions" element={<TrustTransactions />} />
        <Route path="remittances" element={<TrustRemittances />} />
        <Route path="reconciliation" element={<TrustReconciliation />} />
        <Route path="close" element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'SystemAdmin']}>
            <TrustClosePeriod />
          </ProtectedRoute>
        } />
        <Route path="reports" element={<TrustReports />} />
        {/* R10e36.68.4-F831: Commission Invoicing route for Design A collectors */}
        <Route path="commission-invoices" element={<CommissionInvoices />} />
      </Route>
      
      {/* Admin Routes */}
      <Route
        path="/admin/roster"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <JurisdictionRoster />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/onboarding"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <CollectorOnboarding />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/onboarding/:sessionId"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <CollectorOnboarding />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.71.1-F778: First-Time Setup Wizard - desktop only */}
      <Route
        path="/collector/first-time-setup"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'CollectorStaff']}>
            <DesktopOnly pageName="First-Time Setup">
              <FirstTimeSetupWizard />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/transfers"
        element={
          <ProtectedRoute allowedRoles={['SystemAdmin']}>
            <JurisdictionTransfer />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.71.1-F778: Assessment Changes - desktop only */}
      <Route
        path="/collector/assessment-changes"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'CollectorStaff']}>
            <DesktopOnly pageName="Assessment Changes">
              <AssessmentChanges />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.71.1-F778: Millage Rates - desktop only */}
      <Route
        path="/collector/millage-rates"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'SystemAdmin']}>
            <DesktopOnly pageName="Millage Rates">
              <MillageRateAdjustment />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />

      {/* PC132 (Deploy 7 Batch 3 hotfix): standalone Billing Calendar editor
          — exposes the wizard's dual-cycle (CMF + SCHOOL) UI as a live page. */}
      <Route
        path="/collector/billing-calendar"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'SystemAdmin']}>
            <DesktopOnly pageName="Billing Calendar">
              <BillingCalendarEditor />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />

      {/* PC135: SCHOOL installment plan configurator (PA Act 1) */}
      <Route
        path="/collector/installment-plan"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'SystemAdmin']}>
            <DesktopOnly pageName="Installment Plan">
              <InstallmentPlanConfig />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.62.3-F522: Alias route for SetupChecklist navigation - desktop only */}
      <Route
        path="/collector/millage-adjustment"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'SystemAdmin']}>
            <DesktopOnly pageName="Millage Rates">
              <MillageRateAdjustment />
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.50-F285: Payment Posting Policy Admin */}
      <Route
        path="/admin/posting-policy"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'SystemAdmin']}>
            <PaymentPostingPolicyAdmin />
          </ProtectedRoute>
        }
      />
      
      {/* R10e35b-8.3-009: Outstanding Bills Page */}
      <Route
        path="/collector/bills"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'CollectorStaff', 'SystemAdmin']}>
            <OutstandingBills />
          </ProtectedRoute>
        }
      />
      
      {/* R10e36.73.11-F729: Historical Import Page - R10e36.71.1-F778: desktop only */}
      <Route
        path="/historical-import"
        element={
          <ProtectedRoute allowedRoles={['CollectorAdmin', 'SystemAdmin']}>
            <DesktopOnly pageName="Historical Import">
              <DashboardLayout>
                <HistoricalImportPage />
              </DashboardLayout>
            </DesktopOnly>
          </ProtectedRoute>
        }
      />
      
      <Route path="/" element={<Navigate to={IS_ORDER_HOST ? '/portal' : '/login'} replace />} />
      
      <Route
        path="/unauthorized"
        element={
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">403 Unauthorized</h1>
              <p className="text-slate-600">You don&apos;t have permission to access this page.</p>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

// R10e36.71.1-F773: SudoModal provider wrapper
function SudoModalProvider({ children }) {
  const { showModal, setShowModal, handleSuccess } = useSudoModal();
  return (
    <>
      {children}
      <SudoModal 
        open={showModal} 
        onOpenChange={setShowModal} 
        onSuccess={handleSuccess} 
      />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <GuidedTourProvider>
          <SudoModalProvider>
            <div className="App">
              <Suspense fallback={<LoadingSpinner />}>
                <AppRoutes />
              </Suspense>
              <Toaster />
            </div>
          </SudoModalProvider>
        </GuidedTourProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
